import type {
  ColumnSetting,
  FieldBlock,
  FieldBlockSettingsBookings,
  FieldBlockSettingsBookingsInput,
  FieldBlockSettingsFoldersInput,
  FieldBlockSettingsNotes,
  FieldBlockSettingsNotesInput,
  FieldBlockSettingsProductPrice,
  FieldBlockSettingsProductPriceInput,
} from "@scayla/api-client-fetch";
import { FieldBlockType, FileFolderType, NotesType, NotesVisibility } from "@scayla/api-client-fetch";

function isFoldersSettings(settings: FieldBlock["settings"]): settings is FieldBlockSettingsFoldersInput {
  return !!settings && typeof settings === "object" && "type" in settings && "columns" in settings;
}

export function sanitizeFieldBlockSettings(
  settings: FieldBlock["settings"],
  blockType?: FieldBlockType,
): FieldBlock["settings"] {
  if (!settings) return settings;

  switch (blockType) {
    case FieldBlockType.FOLDERS: {
      if (!isFoldersSettings(settings)) return settings;
      return {
        type: settings.type,
        columns: Array.isArray(settings.columns) ? settings.columns : [],
      } satisfies FieldBlockSettingsFoldersInput;
    }
    case FieldBlockType.BOOKINGS: {
      const s = settings as FieldBlockSettingsBookings;
      const out: FieldBlockSettingsBookingsInput = {
        columns: Array.isArray(s.columns) ? s.columns : [],
      };
      if (typeof s.showFilterRow === "boolean") {
        out.showFilterRow = s.showFilterRow;
      }
      return out;
    }
    case FieldBlockType.PRODUCT_PRICE: {
      const s = settings as FieldBlockSettingsProductPrice;
      const out: FieldBlockSettingsProductPriceInput = {
        columns: Array.isArray(s.columns) ? s.columns : [],
      };
      if (typeof s.showFilterRow === "boolean") {
        out.showFilterRow = s.showFilterRow;
      }
      return out;
    }
    case FieldBlockType.NOTES: {
      const s = settings as FieldBlockSettingsNotes;
      const out: FieldBlockSettingsNotesInput = { type: s.type, note: s.note ?? "" };
      if (s.visibility !== undefined) out.visibility = s.visibility;
      return out;
    }
    default:
      if (isFoldersSettings(settings)) {
        return {
          type: settings.type,
          columns: Array.isArray(settings.columns) ? settings.columns : [],
        } satisfies FieldBlockSettingsFoldersInput;
      }
      return settings;
  }
}

export function newFoldersFieldBlockSettingsInput(
  type: FileFolderType,
  columns: ColumnSetting[] = [],
): FieldBlockSettingsFoldersInput {
  return {
    type,
    columns,
  } satisfies FieldBlockSettingsFoldersInput;
}

export function newNotesFieldBlockSettingsInput(
  type: NotesType,
  note = "",
  visibility?: NotesVisibility,
): FieldBlockSettingsNotesInput {
  const out: FieldBlockSettingsNotesInput = { type, note };
  if (visibility !== undefined) out.visibility = visibility;
  return out;
}

export function newBookingsFieldBlockSettingsInput(
  columns: ColumnSetting[],
  showFilterRow?: boolean,
): FieldBlockSettingsBookingsInput {
  const out: FieldBlockSettingsBookingsInput = {
    columns: Array.isArray(columns) ? columns : [],
  };
  if (typeof showFilterRow === "boolean") {
    out.showFilterRow = showFilterRow;
  }
  return out;
}

export function newProductPriceFieldBlockSettingsInput(
  columns: ColumnSetting[],
  showFilterRow?: boolean,
): FieldBlockSettingsProductPriceInput {
  const out: FieldBlockSettingsProductPriceInput = {
    columns: Array.isArray(columns) ? columns : [],
  };
  if (typeof showFilterRow === "boolean") {
    out.showFilterRow = showFilterRow;
  }
  return out;
}
